/**
 * Filter Properties Screen (Authenticated)
 *
 * This screen uses the SpacesSearchView component in authenticated mode
 * to display workspace listings for logged-in hosts/admins.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import SpacesSearchView from "../../components/SpacesSearchView";
import ReusableBookingModal from "../../components/ReusableBookingModal";
import useBookingFlow from "../../hooks/useBookingFlow";
import { showApiMessage } from "../../utils/messageHandler";
import { spaceManagementService } from "../../services/host-apiServices/spaceManagementService";
import { AnalyticsEvents, AnalyticsProps, trackAnalyticsEvent } from "../../utils/analytics";
import {
  readPendingBookingSelectionForSpace,
  clearPendingBookingSelection,
} from "../../utils/pendingBookingSelection";
import logger from "../../utils/logger";


const FilterProperties = () => {
  const history = useHistory();
  const location = useLocation();
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [existingBookings, setExistingBookings] = useState([]);
  const [availabilityInstanceIds, setAvailabilityInstanceIds] = useState([]);
  const [restoredSelection, setRestoredSelection] = useState(null);
  const spaceDetailsCacheRef = useRef({});

  // Check for space selected from search
  const [preselectedSpace] = useState(() => {
    const spaceFromSearch = sessionStorage.getItem("selectedSpaceFromSearch");
    if (!spaceFromSearch) {
      return null;
    }

    try {
      const parsedSpace = JSON.parse(spaceFromSearch);
      sessionStorage.removeItem("selectedSpaceFromSearch");
      return parsedSpace;
    } catch (error) {
      logger.error("Error parsing selected space from search:", error);
      sessionStorage.removeItem("selectedSpaceFromSearch");
      return null;
    }
  });

  // Use centralized booking flow hook for resetBookingError
  const { resetBookingError } = useBookingFlow();

  const getSpaceDurationType = useCallback(space => {
    if (!space) return "HOURLY";
    const candidates = [
      space.duration,
      space.bookingDuration,
      space.bookingPeriod,
      space.billingPeriod,
      space?.propertyPrice?.duration,
    ];
    const match = candidates.find(value => typeof value === "string" && value.trim().length > 0);
    return (match || "HOURLY").toUpperCase();
  }, []);

  const normalizeSpaceForBooking = useCallback(
    space => {
      if (!space) return null;
      const durationType = getSpaceDurationType(space);
      const currentDuration = typeof space.duration === "string" ? space.duration.toUpperCase() : null;
      if (currentDuration === durationType) {
        return space;
      }
      return {
        ...space,
        duration: durationType,
      };
    },
    [getSpaceDurationType]
  );

  const hydrateSpaceDetails = useCallback(
    async baseSpace => {
      if (!baseSpace?.id) return baseSpace;

      const cachedDetails = spaceDetailsCacheRef.current[baseSpace.id];
      if (cachedDetails) {
        return normalizeSpaceForBooking({ ...baseSpace, ...cachedDetails });
      }

      try {
        const response = await spaceManagementService.getSpaceById(Number(baseSpace.id));
        if (response?.status === 200 && response.data) {
          spaceDetailsCacheRef.current[baseSpace.id] = response.data;
          return normalizeSpaceForBooking({ ...baseSpace, ...response.data });
        }
      } catch (error) {
        logger.error("Error loading space details:", error);
      }

      return baseSpace;
    },
    [normalizeSpaceForBooking]
  );

  // Generate all 48 time slots (24 hours in 30-minute chunks) and mark availability (HOURLY only)
  const generateAvailableTimeSlots = useCallback(
    (space, date) => {
      if (!space || !date) return [];
      if (getSpaceDurationType(space) !== "HOURLY") {
        return [];
      }

      const referenceDate = new Date(date);
      const dayName = referenceDate.toLocaleDateString("en-US", { weekday: "long" }).toUpperCase();
      const dayAvailability = space.availableSlots?.filter(slot => slot.day === dayName) || [];

      // Check if selected date is today
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const selectedDay = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), referenceDate.getDate());
      const isToday = selectedDay.getTime() === today.getTime();

      // Generate all 48 slots (24 hours × 30-minute intervals)
      const slots = [];
      for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 30) {
          const slotStartTime = new Date(referenceDate);
          slotStartTime.setHours(hour, minute, 0, 0);

          const slotEndTime = new Date(slotStartTime);
          slotEndTime.setMinutes(slotEndTime.getMinutes() + 30);

          // Check if this slot falls within any availability window for this day
          const slotTotalMinutes = hour * 60 + minute;

          const isAvailable = dayAvailability.some(avail => {
            const [startHours, startMinutes] = avail.startTime.split(":").map(Number);
            const [endHours, endMinutes] = avail.endTime.split(":").map(Number);
            const startTotalMinutes = startHours * 60 + startMinutes;
            const endTotalMinutes = endHours * 60 + endMinutes;

            // Slot is available if it's within the availability window
            return slotTotalMinutes >= startTotalMinutes && slotTotalMinutes < endTotalMinutes;
          });

          // If today, hide slots that are in the past
          if (isToday && slotStartTime.getTime() < now.getTime()) {
            continue; // Skip past slots for today
          }

          if (isAvailable) {
            slots.push({
              startTime: slotStartTime,
              endTime: slotEndTime,
              available: true,
              timeLabel: `${slotStartTime.toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              })} - ${slotEndTime.toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              })}`,
            });
          }
        }
      }

      return slots;
    },
    [getSpaceDurationType]
  );

  const updateTimeSlotsForSpace = useCallback(
    (space, date) => {
      if (!space || !date) {
        setAvailableTimeSlots([]);
        return;
      }
      const slots = generateAvailableTimeSlots(space, date);
      setAvailableTimeSlots(slots);
    },
    [generateAvailableTimeSlots]
  );

  // Fetch existing bookings for a space using the availability endpoint
  const fetchExistingBookings = async spaceId => {
    if (!spaceId) {
      setExistingBookings([]);
      setAvailabilityInstanceIds([]);
      return;
    }

    try {
      const availabilityResponse = await spaceManagementService.getBookingAvailability({
        spaceId: spaceId,
      });

      if (availabilityResponse.status === 200 && availabilityResponse.data) {
        const { bookedSlots, instanceIds } = availabilityResponse.data;

        // Transform bookedSlots to match existingBookings format
        const bookings = (bookedSlots || []).map(slot => ({
          spaceInstanceId: slot.spaceInstanceId,
          startTime: slot.startTime,
          endTime: slot.endTime,
          status: slot.status,
        }));
        setExistingBookings(bookings);

        // Store instance IDs from availability response
        setAvailabilityInstanceIds(instanceIds || []);
      } else {
        setExistingBookings([]);
        setAvailabilityInstanceIds([]);
      }
    } catch (error) {
      logger.error("Error fetching booking availability:", error);
      setExistingBookings([]);
      setAvailabilityInstanceIds([]);
    }
  };

  const handleSpaceClick = async (space, updateUrl = true) => {
    if (!space) return;

    const normalizedSpace = normalizeSpaceForBooking(space);

    // Sync to URL if requested
    if (updateUrl && normalizedSpace?.id) {
      const searchParams = new URLSearchParams(location.search);
      if (searchParams.get("spaceId") !== String(normalizedSpace.id)) {
        searchParams.set("spaceId", String(normalizedSpace.id));
        history.push({ search: searchParams.toString() });
      }
    }

    // Check for restored pending booking selection for this space
    if (normalizedSpace?.id) {
      const pending = readPendingBookingSelectionForSpace(Number(normalizedSpace.id));
      if (pending) {
        setRestoredSelection(pending);
        if (pending.window?.startTime) {
          const restoredDate = new Date(pending.window.startTime);
          if (!Number.isNaN(restoredDate.getTime())) {
            setSelectedDate(restoredDate);
            updateTimeSlotsForSpace(normalizedSpace, restoredDate);
          }
        }
      }
    }

    // Check if it's a potential Play service first to avoid opening modal optimistically
    const isPlay = space?.category?.name === "Play" || space?.isPlayService;

    trackAnalyticsEvent(AnalyticsEvents.LISTING_OPENED, {
      [AnalyticsProps.LISTING_ID]: normalizedSpace?.id,
      [AnalyticsProps.LISTING_NAME]: normalizedSpace?.name || normalizedSpace?.title,
      [AnalyticsProps.PROPERTY_TYPE]: normalizedSpace?.category?.name || normalizedSpace?.propertyType || "other",
      [AnalyticsProps.IS_PLAY_SERVICE]: !!isPlay,
    });

    if (isPlay) {
      // Fetch details first to check tickets
      const hydratedSpace = await hydrateSpaceDetails(normalizedSpace);
      const tickets = hydratedSpace?.tickets || hydratedSpace?.spaceTickets || [];
      const isOfflinePlayService = tickets.length > 0 && tickets.every(t => t.ticketType === "OFFLINE");

      if (isOfflinePlayService) {
        const ticketUrl = tickets.find(t => t.ticketType === "OFFLINE")?.url;
        if (ticketUrl) {
          let finalUrl = ticketUrl;
          if (!finalUrl.startsWith("http://") && !finalUrl.startsWith("https://")) {
            finalUrl = "https://" + finalUrl;
          }
          window.open(finalUrl, "_blank");
          return; // Skip dialog entirely
        }
      }
    }

    setSelectedSpace(normalizedSpace);
    setIsModalOpen(true);
    // Generate time slots for current date
    updateTimeSlotsForSpace(normalizedSpace, selectedDate);
    // Fetch existing bookings for conflict checking
    await fetchExistingBookings(normalizedSpace.id);
    // Reset any previous booking errors when opening modal
    resetBookingError();

    // Hydrate space with latest data (ensures duration and availability stay in sync)
    const hydratedSpace = await hydrateSpaceDetails(normalizedSpace);
    if (hydratedSpace && hydratedSpace !== normalizedSpace) {
      // Double check if the hydrated space reveals it's an offline play service (edge case)
      const tickets = hydratedSpace?.tickets || hydratedSpace?.spaceTickets || [];
      const isOfflinePlayService =
        (hydratedSpace?.category?.name === "Play" || hydratedSpace?.isPlayService) &&
        tickets.length > 0 &&
        tickets.every(t => t.ticketType === "OFFLINE");

      if (isOfflinePlayService) {
        const ticketUrl = tickets.find(t => t.ticketType === "OFFLINE")?.url;
        if (ticketUrl) {
          let finalUrl = ticketUrl;
          if (!finalUrl.startsWith("http://") && !finalUrl.startsWith("https://")) {
            finalUrl = "https://" + finalUrl;
          }
          setIsModalOpen(false); // Close it if it was opened
          setSelectedSpace(null);
          window.open(finalUrl, "_blank");
          return;
        }
      }

      setSelectedSpace(hydratedSpace);
      updateTimeSlotsForSpace(hydratedSpace, selectedDate);
    }
  };

  // Listen for spaceId / space in URL query params and open automatically
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const spaceIdParam = searchParams.get("spaceId") || searchParams.get("space");
    if (spaceIdParam) {
      const parsedId = Number(spaceIdParam);
      if (!Number.isNaN(parsedId)) {
        // If already selected and matching, no need to re-fetch
        if (selectedSpace?.id === parsedId && isModalOpen) {
          return;
        }

        const pending = readPendingBookingSelectionForSpace(parsedId);
        if (pending) {
          setRestoredSelection(pending);
        }

        spaceManagementService
          .getSpaceById(parsedId)
          .catch(() => spaceManagementService.getPublicSpaceById(parsedId))
          .then(response => {
            const data = response?.data?.data || response?.data;
            if (data) {
              handleSpaceClick(data, false);
            }
          })
          .catch(error => {
            logger.error("Error loading space from URL param:", error);
          });
      }
    }
  }, [location.search]);

  const handleDateChange = newDate => {
    setSelectedDate(newDate);
    if (selectedSpace && newDate) {
      updateTimeSlotsForSpace(selectedSpace, newDate);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSpace(null);
    setAvailableTimeSlots([]);
    setRestoredSelection(null);
    resetBookingError();

    // Clean up spaceId from URL query param if present
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has("spaceId") || searchParams.has("space")) {
      searchParams.delete("spaceId");
      searchParams.delete("space");
      history.push({ search: searchParams.toString() });
    }
  };

  // Create initial filters for preselected space
  const initialFilters = useMemo(() => {
    if (!preselectedSpace?.id) {
      return {};
    }

    const numericId = Number(preselectedSpace.id);
    return {
      id: Number.isNaN(numericId) ? preselectedSpace.id : numericId,
    };
  }, [preselectedSpace]);

  return (
    <>
      <SpacesSearchView
        mode="authenticated"
        showBackButton={false}
        title="Spaces & Services"
        subtitle="Manage and view your workspace listings"
        pageSize={20}
        initialFilters={initialFilters}
        onSpaceClick={handleSpaceClick}
      />

      {isModalOpen && selectedSpace && (
        <ReusableBookingModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          space={selectedSpace}
          initialSelection={restoredSelection || undefined}
          selectedDate={selectedDate}
          availableTimeSlots={availableTimeSlots}
          onDateChange={handleDateChange}
          existingBookings={existingBookings}
          availabilityInstanceIds={availabilityInstanceIds}
          userCurrency={selectedSpace?.currency || "KES"}
          timeFormat="24h"
          showBookingSummary={true}
          showPaymentOptions={true}
          closeOnSuccess={false}
          onBookingSuccess={(response, operation) => {
            clearPendingBookingSelection();
            // Refresh bookings after successful booking
            if (selectedSpace?.id) {
              fetchExistingBookings(selectedSpace.id);
            }
          }}
          onBookingError={(error, operation) => {
            // Avoid double surfacing errors when payment flow already shows its own dialog
            if (operation !== "create booking with pending payment") {
              showApiMessage(error, operation);
            }
          }}
        />
      )}
    </>
  );
};

export default FilterProperties;
